<template>
  <div class="mega-menu-container">
    <div class="d-flex justify-content center mb-3">
      <input
        v-model="searchTerm"
        type="text"
        id="search-input"
        placeholder="Search tenants..."
        class="form-control"
      />
    </div>
    <p class="text-center text-small text-success text-muted mb-3 d-none d-lg-block">
      <em>Click on Tenant name to select</em>
      <br />
      <em>Use arrow to expand & see child(ren)</em>
    </p>

    <div class="mega-menu-desktop" v-if="!isMobile">
      <div class="tree-menu">
        <TreeNode
          v-for="node in filteredMenuData"
          :key="node.key"
          :node="node"
          :expandedNodes="expandedNodes"
          :selectedNode="selectedNode"
          :level="0"
          @toggle="toggleNode"
          :searchTerm="searchTerm"
          @hover="showMegaPanel"
          @select="selectNode"
        />
      </div>
    </div>

    <div class="mega-menu-mobile" v-else>
      <div class="mobile-scrollable">
        <div
          v-for="company in filteredMenuData"
          :key="company.key"
          class="mobile-accordion"
        >
          <div class="mobile-accordion-header">
            <i :class="company.icon || 'fas fa-building'"></i>
            <span @click.stop="selectNode(company)">{{ company.label }}</span>
            <i
              class="fas"
              :class="expandedNodes[company.key] ? 'fa-chevron-down' : 'fa-chevron-right'"
              @click.stop="toggleMobileNode(company.key)"
            ></i>
          </div>
          <transition name="accordion">
            <div class="mobile-accordion-content" v-show="expandedNodes[company.key]">
              <div
                v-for="branch in company.children"
                :key="branch.key"
                class="mobile-accordion-item"
              >
                <div class="mobile-accordion-header">
                  <i :class="branch.icon || 'fas fa-code-branch'"></i>
                  <span @click.stop="selectNode(branch)">{{ branch.label }}</span>
                  <i
                    class="fas"
                    :class="
                      expandedNodes[branch.key] ? 'fa-chevron-down' : 'fa-chevron-right'
                    "
                    @click="toggleMobileNode(branch.key)"
                  ></i>
                </div>

                <div class="mobile-accordion-content" v-show="expandedNodes[branch.key]">
                  <div
                    v-for="subsidiary in branch.children"
                    :key="subsidiary.key"
                    class="mobile-accordion-item"
                  >
                    <div class="mobile-accordion-header">
                      <!-- Chevron: toggle -->
                      <i
                        v-if="
                          Array.isArray(subsidiary.children) && subsidiary.children.length
                        "
                        class="fas"
                        :class="
                          expandedNodes[subsidiary.key]
                            ? 'fa-chevron-down'
                            : 'fa-chevron-right'
                        "
                        @click.stop="toggleMobileNode(subsidiary.key)"
                      ></i>

                      <!-- Label: select -->
                      <span @click.stop="selectNode(subsidiary)">
                        {{ subsidiary.label }}
                      </span>
                    </div>

                    <!-- Grandchildren -->
                    <transition name="accordion">
                      <div
                        class="mobile-accordion-content"
                        v-show="expandedNodes[subsidiary.key]"
                      >
                        <div
                          v-for="child in subsidiary.children"
                          :key="child.key"
                          class="mobile-accordion-item"
                        >
                          <div class="mobile-accordion-header">
                            <!-- Chevron: toggle -->
                            <i
                              v-if="
                                Array.isArray(child.children) && child.children.length
                              "
                              class="fas"
                              :class="
                                expandedNodes[child.key]
                                  ? 'fa-chevron-down'
                                  : 'fa-chevron-right'
                              "
                              @click.stop="toggleMobileNode(child.key)"
                            ></i>

                            <!-- Label: select -->
                            <span @click.stop="selectNode(child)">
                              {{ child.label }}
                            </span>
                          </div>

                          <!-- Great-grandchildren (4th level) -->
                          <transition name="accordion">
                            <div
                              class="mobile-accordion-content"
                              v-show="expandedNodes[child.key]"
                            >
                              <div
                                v-for="grandChild in child.children"
                                :key="grandChild.key"
                                class="mobile-accordion-item"
                                @click.stop="selectNode(grandChild)"
                              >
                                <i :class="grandChild.icon || 'fas fa-user'"></i>
                                <span>{{ grandChild.label }}</span>
                              </div>
                            </div>
                          </transition>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import TreeNode from "./TreeNode.vue";
import debounce from "lodash.debounce";

const props = defineProps({
  menuData: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const emit = defineEmits(["nodeSelect"]);

const expandedNodes = ref({});
const hoveredNode = ref(null);
const selectedNode = ref(null);
const isMobile = ref(false);
const searchTerm = ref("");
const debouncedSearch = ref("");
const initialExpanded = ref(false);

const updateSearch = debounce((value) => {
  debouncedSearch.value = value;
}, 300);

watch(searchTerm, (newVal) => {
  updateSearch(newVal);
});

const filteredMenuData = computed(() => {
  if (!searchTerm.value.trim()) {
    if (!initialExpanded.value) {
      expandFirstPath(props.menuData);
    }
    return props.menuData;
  }

  const term = searchTerm.value.toLowerCase();

  const filterTree = (nodes) => {
    const result = [];

    for (const node of nodes) {
      const match = node.label.toLowerCase().includes(term);
      const children = node.children ? filterTree(node.children) : [];

      if (match || children.length > 0) {
        // Auto-expand matching nodes and their parents
        expandedNodes.value[node.key] = true;

        result.push({
          ...node,
          children,
        });
      }
    }

    return result;
  };

  return filterTree(props.menuData);
});

const panelColumns = computed(() => {
  if (!hoveredNode.value) return [];

  const columns = [];

  if (hoveredNode.value.children) {
    columns.push({
      title: hoveredNode.value.label,
      icon: hoveredNode.value.icon,
      items: hoveredNode.value.children,
    });
  }

  return columns;
});

const checkMobile = () => {
  isMobile.value = window.innerWidth < 992;
};

const toggleNode = (key) => {
  expandedNodes.value = {
    ...expandedNodes.value,
    [key]: !expandedNodes.value[key],
  };
};

const toggleMobileNode = (key) => {
  expandedNodes.value = {
    ...expandedNodes.value,
    [key]: !expandedNodes.value[key],
  };
};

const showMegaPanel = (node) => {
  hoveredNode.value = node;
};

const hideMegaPanel = () => {
  hoveredNode.value = null;
};

const selectNode = (node) => {
  if (!node || !node.key) return;

  selectedNode.value = node;
  emit("nodeSelect", node);

  // Collapse everything and only expand selected path
  let pathKeys = findPathToNode(props.menuData, node.key);
  if (Array.isArray(pathKeys) && pathKeys.length) {
    let newExpanded = {};
    pathKeys.forEach((key) => {
      newExpanded[key] = true;
    });
    expandedNodes.value = newExpanded;
  }
};

const expandFirstPath = (nodes) => {
  nodes.forEach((node) => {
    expandedNodes.value[node.key] = true;
  });
};
const findPathToNode = (nodes, targetKey, path = []) => {
  for (let node of nodes) {
    if (node.key === targetKey) {
      return [...path, node.key];
    }
    if (node.children && node.children.length > 0) {
      let result = findPathToNode(node.children, targetKey, [...path, node.key]);
      if (result) return result;
    }
  }
  return null;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);

  if (props.menuData.length > 0) {
    expandFirstPath(props.menuData);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>
